import { DateTime } from 'luxon'
import type { BookEventFormValues } from '../BookingEvent'
import type { ParsedBookableEvent } from '../types'

const PENDING_CHECKOUT_STORAGE_KEY = 'tebuto-widget-pending-checkout'
const PENDING_CHECKOUT_TTL_MS = 30 * 60 * 1000

type SerializedParsedBookableEvent = Omit<ParsedBookableEvent, 'startDatetime' | 'endDatetime'> & {
    startDatetime: string
    endDatetime: string
}

export type PendingCheckoutSnapshot = {
    scopeKey: string
    savedAt: number
    bookingEvent: SerializedParsedBookableEvent
    bookingData: BookEventFormValues
    publicBookingSiteId?: string
}

function serializeBookingEvent(event: ParsedBookableEvent): SerializedParsedBookableEvent {
    return {
        ...event,
        startDatetime: event.startDatetime.toISO() ?? event.start,
        endDatetime: event.endDatetime.toISO() ?? event.end
    }
}

function deserializeBookingEvent(event: SerializedParsedBookableEvent): ParsedBookableEvent {
    return {
        ...event,
        startDatetime: DateTime.fromISO(event.startDatetime) as DateTime<true>,
        endDatetime: DateTime.fromISO(event.endDatetime) as DateTime<true>
    }
}

export function savePendingCheckout(snapshot: {
    scopeKey: string
    bookingEvent: ParsedBookableEvent
    bookingData: BookEventFormValues
    publicBookingSiteId?: string
}): void {
    if (typeof window === 'undefined') {
        return
    }

    const payload: PendingCheckoutSnapshot = {
        scopeKey: snapshot.scopeKey,
        savedAt: Date.now(),
        bookingEvent: serializeBookingEvent(snapshot.bookingEvent),
        bookingData: snapshot.bookingData,
        ...(snapshot.publicBookingSiteId ? { publicBookingSiteId: snapshot.publicBookingSiteId } : {})
    }

    try {
        window.sessionStorage.setItem(PENDING_CHECKOUT_STORAGE_KEY, JSON.stringify(payload))
    } catch {
        // sessionStorage may be unavailable in private mode or strict embed contexts
    }
}

export function loadPendingCheckout(scopeKey: string): {
    bookingEvent: ParsedBookableEvent
    bookingData: BookEventFormValues
    publicBookingSiteId?: string
} | null {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const raw = window.sessionStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY)
        if (!raw) {
            return null
        }

        const parsed = JSON.parse(raw) as PendingCheckoutSnapshot
        if (parsed.scopeKey !== scopeKey) {
            return null
        }

        if (Date.now() - parsed.savedAt > PENDING_CHECKOUT_TTL_MS) {
            clearPendingCheckout()
            return null
        }

        return {
            bookingEvent: deserializeBookingEvent(parsed.bookingEvent),
            bookingData: parsed.bookingData,
            publicBookingSiteId: parsed.publicBookingSiteId
        }
    } catch {
        clearPendingCheckout()
        return null
    }
}

export function clearPendingCheckout(): void {
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.sessionStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY)
    } catch {
        // ignore
    }
}
